import {useQuery} from '@apollo/client'
import {IResPoliciesType} from '../../types/policies'
import {GET_ALL_POLICIES_TYPES} from '../../gql/queries/policiesQueries'
import {IResCarriers} from '../../types/carriers'
import {GET_ALL_INSURANCE_OPERATORS_LIST, GET_ONE_CARRIER} from '../../gql/queries/carriersQuery'
import {IResStates} from '../../types/statesEU'
import {GET_STATES_EU} from '../../gql/queries/statesEUQuery'
import {isMobileDevice} from '../../../../_metronic/assets/ts/_utils'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {defineMessages, useIntl} from 'react-intl'
import {Col, Form, Row, Spinner} from 'react-bootstrap'
import DatePicker from 'react-multi-date-picker'
import {STATUS_POLICY} from '../../helpers/consts'
import Select from 'react-select'
import {QuoteCount} from './components/QuoteCount'
import {QuoteReportUsers} from './components/QuoteReportUsers'

const M = defineMessages({
  pageTitle: {id: 'dashboard.quotes.page.title', defaultMessage: 'Quotes reports'},
  selectStatus: {
    id: 'dashboard.quotes.filter.selectStatus',
    defaultMessage: 'Select policy status',
  },
  selectPolicyType: {
    id: 'dashboard.quotes.filter.selectPolicyType',
    defaultMessage: 'Select policy type',
  },
  selectCarrier: {id: 'dashboard.quotes.filter.selectCarrier', defaultMessage: 'Select carrier'},
  selectState: {id: 'dashboard.quotes.filter.selectState', defaultMessage: 'Select state'},
  policyType: {id: 'dashboard.policies.page.filter.policyType', defaultMessage: 'Policy Type'},
  carriers: {id: 'dashboard.policies.page.filter.carriers', defaultMessage: 'Carriers'},
  states: {id: 'dashboard.policies.page.filter.states', defaultMessage: 'States'},
  notFound: {id: 'dashboard.policies.page.placeholder.notFound', defaultMessage: 'Not found'},
  completed: {id: 'common.status.completed', defaultMessage: 'Completed'},
  pending: {id: 'common.status.pending', defaultMessage: 'Pending'},
  canceled: {id: 'common.status.canceled', defaultMessage: 'Canceled'},
  errorWithMessage: {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
})

export const HomeQuotesPage = () => {
  const intl = useIntl()
  const {
    data: dataPolicyTypes,
    loading: loadingPolicyTypes,
    error: errorPolicyTypes,
  } = useQuery<IResPoliciesType>(GET_ALL_POLICIES_TYPES, {fetchPolicy: 'cache-first'})
  const {
    data: dataCarriers,
    loading: loadingCarriers,
    error: errorCarriers,
  } = useQuery<IResCarriers>(GET_ALL_INSURANCE_OPERATORS_LIST, {fetchPolicy: 'no-cache'})
  const {
    data: dataStates,
    loading: loadingStates,
    error: errorStates,
  } = useQuery<IResStates>(GET_STATES_EU, {fetchPolicy: 'no-cache'})

  const isMobile = isMobileDevice()

  const [dates, setDates] = useState([
    new Date(new Date(new Date().setDate(1)).setHours(0, 0, 0, 0)),
    new Date(new Date().setHours(23, 59, 59, 999)),
  ])

  const [policyStatus, setPolicyStatus] = useState('')

  const [policyTypeSelect, setPolicyTypeSelect] = useState<null | {value: string; label: string}>(
    null
  )
  const [carrierSelect, setCarrierSelect] = useState<null | {value: string; label: string}>(null)
  const [stateSelect, setStateSelect] = useState<null | {value: string; label: string}>(null)

  useEffect(() => {
    if (errorCarriers)
      toast.error(intl.formatMessage(M.errorWithMessage, {message: errorCarriers.message}))
  }, [errorCarriers, intl])

  useEffect(() => {
    if (errorPolicyTypes)
      toast.error(intl.formatMessage(M.errorWithMessage, {message: errorPolicyTypes.message}))
  }, [errorPolicyTypes, intl])

  useEffect(() => {
    if (errorStates)
      toast.error(intl.formatMessage(M.errorWithMessage, {message: errorStates.message}))
  }, [errorStates, intl])



  return (
    <div>
      <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between'>
        <h1 className='d-flex align-items-center'>
          {intl.formatMessage(M.pageTitle)}
          {(loadingCarriers || loadingPolicyTypes || loadingStates) && (
            <Spinner animation='border' size='sm' className='ms-1' />
          )}
        </h1>
        <div className='d-flex flex-column flex-sm-row align-items-start gap-3 w-100 w-md-auto'>
          <DatePicker
            style={{
              padding: '1.55em 1em',
              textAlign: 'center',
              flex: '1',
              width: '100%',
              minWidth: '215px',
            }}
            containerStyle={{width: '100%'}}
            value={dates}
            onChange={(d) => {
              if (d.length <= 1) return
              const [d1, d2] = d
              const date1 = d1.toDate()
              const date2 = d2.toDate()
              date1.setHours(0, 0, 0, 0)
              date2.setHours(23, 59, 59, 99)
              setDates([date1, date2])
            }}
            range
            numberOfMonths={isMobile ? 1 : 2}
            format='DD-MMM-YYYY'
          />
          <Form.Select value={policyStatus} onChange={(v) => setPolicyStatus(v.target.value)}>
            <option value=''>{intl.formatMessage(M.selectStatus)}</option>
            <option value={STATUS_POLICY.PENDING}>{intl.formatMessage(M.pending)}</option>
            <option value={STATUS_POLICY.COMPLETED}>{intl.formatMessage(M.completed)}</option>
            <option value={STATUS_POLICY.CANCELED}>{intl.formatMessage(M.canceled)}</option>
          </Form.Select>
        </div>
      </div>
      <Row className='mt-3 g-3'>
        <Col sm={12} md={4}>
          <small>{intl.formatMessage(M.policyType)}</small>
          <Select
            options={
              dataPolicyTypes?.allPoliciesType.edges.map(({node}) => ({
                label: node.typePolicy,
                value: node.id,
              })) || []
            }
            placeholder={intl.formatMessage(M.selectPolicyType)}
            noOptionsMessage={() => intl.formatMessage(M.notFound)}
            value={policyTypeSelect}
            onChange={setPolicyTypeSelect}
            isClearable
          />
        </Col>
        <Col sm={12} md={4}>
          <small>{intl.formatMessage(M.carriers)}</small>
          <Select
            options={
              dataCarriers?.allInsuranceOperators.edges.map(({node}) => ({
                label: node.name,
                value: node.id,
              })) || []
            }
            placeholder={intl.formatMessage(M.selectCarrier)}
            noOptionsMessage={() => intl.formatMessage(M.notFound)}
            value={carrierSelect}
            onChange={setCarrierSelect}
            isClearable
          />
        </Col>
        <Col sm={12} md={4}>
          <small>{intl.formatMessage(M.states)}</small>
          <Select
            options={
              dataStates?.allStates.edges.map(({node}) => ({
                label: node.name,
                value: node.id,
              })) || []
            }
            placeholder={intl.formatMessage(M.selectState)}
            noOptionsMessage={() => intl.formatMessage(M.notFound)}
            value={stateSelect}
            onChange={setStateSelect}
            isClearable
          />
        </Col>
      </Row>

      <QuoteCount
        start={dates[0]}
        end={dates[1]}
        policyType={policyTypeSelect?.value}
        carriers={carrierSelect?.value}
        states={stateSelect?.value}
        status={policyStatus}
      />

      <div className='mt-4'>
        <QuoteReportUsers start={dates[0]} end={dates[1]} />
      </div>
    </div>
  )
}
